import { CombinedGraphQLErrors } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";

/**
 * THE GRAPHQL ERROR ENTRY READERS, for App and Book (consistency batch c4).
 *
 * Moved here UNCHANGED from the per-app copies: `entriesFrom` and its body
 * parse from Book's `features/booking/operations.ts`, `hasCode` from Book's
 * `auth/clientAccount.ts` (`isGraphQLError`), and the CombinedGraphQLErrors
 * read App's `features/stripeConnect/errors.ts` made inline. Every caller
 * classifies with the same entries it read before; only where they come from
 * changed.
 *
 * TWO FAMILIES, and they are not interchangeable:
 *
 *   EVERY SHAPE (`entriesFrom`): Apollo 4 `CombinedGraphQLErrors.errors`, the
 *     Apollo 3 `graphQLErrors`, a non-2xx `ServerError`'s raw `bodyText`, and
 *     `cause`. For the booking classifiers, whose backend answers typed
 *     refusals with 4xx bodies (a 409 slot conflict arrives as `bodyText`).
 *   COMBINED ONLY (`graphQLErrorsOf`, `hasCode`): the entries of an Apollo 4
 *     `CombinedGraphQLErrors` and nothing else. A ServerError body is never
 *     read, so a gateway's 4xx/5xx body can never satisfy these.
 *
 * CASE. Codes are compared EXACTLY here, as Backend sends them. Two callers
 * compare case-insensitively on purpose and do it at their own site, because
 * they also differ on a non-string code and folding them together would change
 * one of them: App's `isPermissionDenied` (`String(code).toUpperCase()`, so a
 * numeric 403 counts) and Book's `cancelTransportFailureFrom` (a string code
 * only, so nothing but a real code can claim "definitely not executed").
 *
 * NOT HERE, on purpose: the session-level readers beside this file
 * (`sessionRefusal.ts` `sessionErrorEntries`, `accessRecovery.ts`), whose read
 * order is security-reviewed and capped; and Book's Terms absence check
 * (`features/terms/operations.ts` `termsFieldAbsent`), which needs EVERY entry,
 * its message, and treats an unparseable body as "not absent".
 *
 * No entry cap here, unlike the session readers: the move keeps the behaviour
 * the callers were reviewed with.
 */

/** One error entry: its `extensions.code`, and the whole `extensions` for sibling fields. */
export interface ErrorEntry {
  code: unknown;
  ext: Record<string, unknown>;
}

/** The entries of one `errors[]`-shaped list. Anything that is not an object is skipped. */
export function entriesOf(list: unknown): ErrorEntry[] {
  const out: ErrorEntry[] = [];
  if (!Array.isArray(list)) return out;
  for (const entry of list) {
    if (entry && typeof entry === "object") {
      const ext = ((entry as { extensions?: unknown }).extensions ?? {}) as Record<string, unknown>;
      out.push({ code: ext.code, ext });
    }
  }
  return out;
}

/**
 * The `errors` of a non-2xx response's raw body (Apollo 4 `ServerError`
 * `bodyText`), or undefined. A body that is not JSON carries no code; it is
 * still evidence that the server answered, which the callers judge, not this.
 */
export function bodyErrorsOf(error: unknown): unknown {
  if (!error || typeof error !== "object") return undefined;
  const { bodyText } = error as { bodyText?: unknown };
  if (typeof bodyText !== "string") return undefined;
  try {
    return (JSON.parse(bodyText) as { errors?: unknown }).errors;
  } catch {
    return undefined;
  }
}

/**
 * Every error entry an Apollo 3/4 error carries, in this order: `errors`,
 * `graphQLErrors`, the parsed `bodyText`, then `cause` (only when it carries
 * a code). Not just the code: validation needs the sibling fields.
 */
export function entriesFrom(error: unknown): ErrorEntry[] {
  if (!error || typeof error !== "object") return [];
  const candidate = error as {
    errors?: unknown;
    graphQLErrors?: unknown;
    cause?: { extensions?: { code?: unknown } };
  };
  const out = [
    ...entriesOf(candidate.errors),
    ...entriesOf(candidate.graphQLErrors),
    ...entriesOf(bodyErrorsOf(error)),
  ];
  if (candidate.cause?.extensions?.code !== undefined) {
    out.push({ code: candidate.cause.extensions.code, ext: candidate.cause.extensions as Record<string, unknown> });
  }
  return out;
}

/** The entries of an Apollo 4 `CombinedGraphQLErrors` (a 200 with errors, or a GraphQL-typed non-2xx); empty for anything else. */
export function graphQLErrorsOf(error: unknown): readonly GraphQLFormattedError[] {
  return CombinedGraphQLErrors.is(error) ? error.errors : [];
}

/**
 * Some `CombinedGraphQLErrors` entry carries exactly `code`, and `detailCode`
 * too when one is given. Exact case; a ServerError body never matches.
 */
export function hasCode(error: unknown, code: string, detailCode?: string): boolean {
  return graphQLErrorsOf(error).some(({ extensions }) =>
    extensions?.code === code && (!detailCode || extensions.detailCode === detailCode)
  );
}
